import { Button, Group, Modal, Stack } from '@mantine/core';
import { get } from 'lodash';
import moment from 'moment';
import { useEffect, useRef } from 'react';

import { DocumentsEnum, EORIGIN_DATE_FORMAT } from '@e-origin/shared';

import { Select } from '@components';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';
import { DocumentFormFactory, DocumentFormSchemaProps, FormDocumentsInputType } from './DocumentFormFactory';

interface DocDetailsProps {
  type: DocumentsEnum;
  dataSource: any;
  dataSourceIndex: number;
}

interface DocumentEditProps {
  opened: boolean;
  onHide: () => void;
  docDetails?: DocDetailsProps;
  editDisabled: boolean;
  disabledDocuments: DocumentsEnum[];
  onSave: (savedDocument: any) => void;
  documentFormSchema: DocumentFormSchemaProps;
}

export const documentsLabels = {
  [DocumentsEnum.PREVIOUS_DOCUMENTS]: 'Previous documents',
  [DocumentsEnum.SUPPORTING_DOCUMENTS]: 'Supporting documents',
  [DocumentsEnum.TRANSPORT_DOCUMENTS]: 'Transport documents',
  [DocumentsEnum.ADDITIONAL_REFERENCE]: 'Additional reference',
  [DocumentsEnum.ADDITIONAL_INFORMATION]: 'Additional information',
};

export const DocumentEdit: React.FC<DocumentEditProps> = ({
  opened,
  onHide,
  docDetails,
  editDisabled = false,
  disabledDocuments = [],
  onSave,
  documentFormSchema,
}) => {
  const documentsTypesList = useRef(
    Object.keys(documentsLabels)
      .filter((docType) => !disabledDocuments.includes(docType as DocumentsEnum))
      .map((docType) => ({ value: docType, label: documentsLabels[docType], 'data-pw': `document-type-option-${docType}` })),
  );

  const buildInitialFieldValues = (type?: DocumentsEnum, data?: any) => {
    if (!type) return {};

    const formSchema = documentFormSchema[type];
    if (!formSchema?.fields) return {};

    const initialData = {};

    Object.keys(formSchema.fields).forEach((fieldPath) => {
      const fieldConfig = formSchema.fields[fieldPath];

      if (fieldConfig.type === FormDocumentsInputType.NUMBER) {
        initialData[fieldPath] = Number(get(data, fieldPath)) || undefined;
      } else if (fieldConfig.type === FormDocumentsInputType.DATE) {
        const dateValue = get(data, fieldPath);
        initialData[fieldPath] = dateValue ? moment(dateValue, EORIGIN_DATE_FORMAT).format(EORIGIN_DATE_FORMAT) : undefined;
      } else {
        initialData[fieldPath] = get(data, fieldPath) || '';
      }
    });

    return initialData;
  };

  const form = useFormWithNameInputProp({
    initialValues: {
      documentType: docDetails?.type || null,
      operation: docDetails?.dataSource?.operation || undefined,
      id: docDetails?.dataSource?.id || {},
      ...buildInitialFieldValues(docDetails?.type, docDetails?.dataSource),
    },
  });

  useEffect(() => {
    if (form.values.documentType) {
      const currentSchema = documentFormSchema[form.values.documentType as DocumentsEnum];
      if (currentSchema?.fields) {
        const newValues = buildInitialFieldValues(form.values.documentType as DocumentsEnum, {});
        form.setValues({
          documentType: form.values.documentType,
          operation: form.values.operation,
          id: form.values.id,
          ...newValues,
        });
      }
    }
  }, [form.values.documentType]);

  useEffect(() => {
    form.setValues({
      documentType: docDetails?.type,
      operation: docDetails?.dataSource?.operation,
      id: docDetails?.dataSource?.id || {},
      ...buildInitialFieldValues(docDetails?.type, docDetails?.dataSource),
    });
  }, [docDetails]);

  const onSubmit = (values) => {
    if (!values.documentType) {
      return;
    }

    const formData = {
      type: values.documentType,
      operation: values.operation,
      docIndex: docDetails?.dataSourceIndex,
      data: {},
    };

    const currentSchema = documentFormSchema[values.documentType as DocumentsEnum];
    if (currentSchema?.fields) {
      const fieldData = {};
      Object.keys(currentSchema.fields).forEach((fieldPath) => {
        fieldData[fieldPath] = values[fieldPath];
      });
      formData.data = fieldData;
    }

    onSave(formData);
    onHide();
  };

  const isDropdownDisabled = editDisabled || (form.values.id && Object.keys(form.values.id).some((key) => !!form.values.id[key]));

  return (
    <Modal title={docDetails ? 'Edit document' : 'Add new document'} opened={opened} onClose={onHide} centered zIndex={9999}>
      <Stack>
        <Select
          {...form.getInputProps('documentType')}
          label="Type of document"
          data={documentsTypesList.current}
          value={form.values.documentType}
          disabled={isDropdownDisabled}
          data-pw="document-type-select"
        />

        <DocumentFormFactory type={form.values.documentType as DocumentsEnum} form={form} editDisabled={editDisabled} documentFormSchema={documentFormSchema} />
      </Stack>
      {!editDisabled && (
        <Group justify="center" mt="xl">
          <Button type="button" key="save-modal-btn" variant="filled" size="lg" onClick={() => onSubmit(form.values)} data-pw="save-doc-btn">
            {docDetails ? 'Update' : 'Add'}
          </Button>
        </Group>
      )}
    </Modal>
  );
};
